import { useId, useState, type ReactElement } from 'react';

import { donutArcs, percentages, ticks } from './logic.js';

/**
 * Graphiques des tableaux de bord, embarqués (HTML et SVG, aucune bibliothèque ni CDN,
 * constitution) : barres verticales, barres horizontales classées, anneau, jauge de
 * progression. Couleurs des jetons `--chart-*` (ordre fixe, validé pour le daltonisme) ;
 * chaque graphique a un titre, une valeur au survol et un tableau équivalent pour les
 * lecteurs d'écran ; le texte reste à sa taille au téléphone.
 */

export interface Datum {
  label: string;
  value: number;
  /** Libellé court sous la barre (sinon `label`). */
  short?: string;
  /** Couleur d'état (sinon la couleur de la série). */
  tone?: 'ok' | 'warning' | 'critical' | undefined;
}

const SERIES = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
];
const TONE_COLOR = {
  ok: 'var(--ok)',
  warning: 'var(--warning)',
  critical: 'var(--critical)',
} as const;

const fr = (n: number): string => n.toLocaleString('fr-FR');
const colorOf = (d: Datum, i = 0): string =>
  d.tone === undefined ? (SERIES[i % SERIES.length] ?? 'var(--chart-1)') : TONE_COLOR[d.tone];

function SrTable({
  title,
  data,
  unit,
}: {
  title: string;
  data: Datum[];
  unit?: string | undefined;
}): ReactElement {
  return (
    <table className="o-visually-hidden">
      <caption>{title}</caption>
      <tbody>
        {data.map((d) => (
          <tr key={d.label}>
            <th scope="row">{d.label}</th>
            <td>
              {fr(d.value)}
              {unit === undefined ? '' : ` ${unit}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Barres verticales d'une seule série (évolution par jour, par créneau…). */
export function BarChart({
  title,
  data,
  unit,
  height = 180,
}: {
  title: string;
  data: Datum[];
  unit?: string;
  height?: number;
}): ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const grid = ticks(Math.max(0, ...data.map((d) => d.value)), 4);
  const max = grid[grid.length - 1] ?? 1;
  const showEvery = Math.ceil(data.length / 12);
  const current = hover === null ? undefined : data[hover];
  return (
    <figure className="o-chart">
      <figcaption className="o-chart__title">{title}</figcaption>
      <div className="o-bars" style={{ height }} aria-hidden="true">
        <div className="o-bars__grid">
          {grid.map((g) => (
            <span
              key={g}
              className="o-bars__gridline"
              style={{ bottom: `${String((g / max) * 100)}%` }}
            >
              <span className="o-bars__tick">{fr(g)}</span>
            </span>
          ))}
        </div>
        <div className="o-bars__plot">
          {data.map((d, i) => (
            <div
              key={d.label}
              className="o-bars__col"
              onMouseEnter={() => {
                setHover(i);
              }}
              onMouseLeave={() => {
                setHover(null);
              }}
            >
              <span className="o-bars__slot">
                <span
                  className="o-bars__bar"
                  data-dim={hover !== null && hover !== i}
                  style={{ height: `${String((d.value / max) * 100)}%`, background: colorOf(d) }}
                />
              </span>
              <span className="o-bars__label">
                {i % showEvery === 0 ? (d.short ?? d.label) : ''}
              </span>
            </div>
          ))}
        </div>
        {current !== undefined && (
          <div className="o-chart__tooltip">
            <strong>{current.label}</strong> : {fr(current.value)}
            {unit === undefined ? '' : ` ${unit}`}
          </div>
        )}
      </div>
      <SrTable title={title} data={data} unit={unit} />
    </figure>
  );
}

/** Barres horizontales classées (répartition par étape, par file…), valeurs écrites. */
export function HBarChart({
  title,
  data,
  unit,
  onSelect,
}: {
  title: string;
  data: Datum[];
  unit?: string;
  onSelect?: (label: string) => void;
}): ReactElement {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <figure className="o-chart">
      <figcaption className="o-chart__title">{title}</figcaption>
      <ul className="o-hbars">
        {data.map((d) => {
          const content = (
            <>
              <span className="o-hbars__label">{d.label}</span>
              <span className="o-hbars__track" aria-hidden="true">
                <span
                  className="o-hbars__bar"
                  style={{ width: `${String((d.value / max) * 100)}%`, background: colorOf(d) }}
                />
              </span>
              <span className="o-hbars__value">
                {fr(d.value)}
                {unit === undefined ? '' : ` ${unit}`}
              </span>
            </>
          );
          return (
            <li key={d.label}>
              {onSelect === undefined ? (
                <div className="o-hbars__row">{content}</div>
              ) : (
                <button
                  type="button"
                  className="o-hbars__row o-hbars__row--button"
                  onClick={() => {
                    onSelect(d.label);
                  }}
                >
                  {content}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </figure>
  );
}

const arcPath = (cx: number, cy: number, r: number, start: number, end: number): string => {
  const sweep = Math.min(end - start, Math.PI * 2 - 0.0001);
  const x1 = cx + r * Math.sin(start);
  const y1 = cy - r * Math.cos(start);
  const x2 = cx + r * Math.sin(start + sweep);
  const y2 = cy - r * Math.cos(start + sweep);
  const large = sweep > Math.PI ? 1 : 0;
  return `M ${String(x1)} ${String(y1)} A ${String(r)} ${String(r)} 0 ${String(large)} 1 ${String(x2)} ${String(y2)}`;
};

/** Anneau de répartition (5 parts au plus, sinon « Autres »), total au centre, légende. */
export function DonutChart({
  title,
  data,
  centerLabel = 'au total',
}: {
  title: string;
  data: Datum[];
  centerLabel?: string;
}): ReactElement {
  const id = useId();
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const parts: Datum[] =
    sorted.length > 5
      ? [
          ...sorted.slice(0, 4),
          { label: 'Autres', value: sorted.slice(4).reduce((s, d) => s + d.value, 0) },
        ]
      : sorted;
  const total = parts.reduce((s, d) => s + d.value, 0);
  const arcs = donutArcs(parts.map((p) => p.value));
  const pct = percentages(parts.map((p) => p.value));
  return (
    <figure className="o-chart o-donut" aria-labelledby={id}>
      <figcaption id={id} className="o-chart__title">
        {title}
      </figcaption>
      <div className="o-donut__body">
        <svg viewBox="0 0 120 120" className="o-donut__svg" aria-hidden="true">
          <circle cx="60" cy="60" r="46" className="o-donut__track" />
          {parts.map((p, i) => {
            const a = arcs[i];
            if (a === undefined || p.value <= 0) return null;
            return (
              <path
                key={p.label}
                d={arcPath(60, 60, 46, a.start, a.end)}
                stroke={colorOf(p, i)}
                className="o-donut__arc"
              >
                <title>{`${p.label} : ${fr(p.value)}`}</title>
              </path>
            );
          })}
          <text x="60" y="60" textAnchor="middle" className="o-donut__total">
            {fr(total)}
          </text>
          <text x="60" y="76" textAnchor="middle" className="o-donut__caption">
            {centerLabel}
          </text>
        </svg>
        <ul className="o-legend">
          {parts.map((p, i) => (
            <li key={p.label}>
              <span
                className="o-legend__swatch"
                style={{ background: colorOf(p, i) }}
                aria-hidden="true"
              />
              <span className="o-legend__label">{p.label}</span>
              <span className="o-legend__value">
                {fr(p.value)} <span className="o-hint">({pct[i] ?? 0} %)</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

/** Jauge de progression (réponses reçues, lectures, places occupées…). */
export function Meter({
  label,
  value,
  max,
  tone,
  detail,
  compact = false,
}: {
  label: string;
  value: number;
  max: number;
  tone?: 'ok' | 'warning' | 'critical' | undefined;
  detail?: string;
  /** Libellé réservé aux lecteurs d'écran (cellule de tableau). */
  compact?: boolean;
}): ReactElement {
  const ratio = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max));
  const pct = Math.round(ratio * 100);
  return (
    <div className={compact ? 'o-meter o-meter--compact' : 'o-meter'}>
      <div className="o-meter__head">
        <span className={compact ? 'o-visually-hidden' : undefined}>{label}</span>
        <span className="o-meter__value">
          {fr(value)} / {fr(max)}
          {detail === undefined ? '' : ` ${detail}`}
        </span>
      </div>
      <div
        className="o-meter__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${fr(value)} sur ${fr(max)} (${String(pct)} %)`}
      >
        <span
          className="o-meter__bar"
          style={{
            width: `${String(pct)}%`,
            background: tone === undefined ? 'var(--chart-1)' : TONE_COLOR[tone],
          }}
        />
      </div>
    </div>
  );
}
